import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Shield, X, LogIn, Users, Calendar, Phone, Mail, Building2,
  RefreshCw, ChevronLeft, ChevronRight, Eye, CheckCircle2,
  Clock, AlertCircle, LogOut, FileText, Search, Menu,
  LayoutDashboard, FolderKanban, ListTodo, Activity, MessageSquare,
  BarChart3, CreditCard, Settings, Plus, Edit, Trash2, Send,
  TrendingUp, DollarSign, UserCheck, Bell, Paperclip, Image, File, Download,
  Rocket, Target, ChevronDown, ChevronUp, CheckSquare, Square, MapPin,
  Mic, MicOff, Volume2, VolumeX, Bot
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBackend = ({ isOpen, onClose }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [token, setToken] = useState(null);
  const [requiresProfileSetup, setRequiresProfileSetup] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  // Location flags for admins
  const getLocationFlag = (username) => {
    const flags = {
      'peter': '🇬🇧',        // Northern Ireland (Union Jack)
      'edward': '🏴󠁧󠁢󠁳󠁣󠁴󠁿',    // Scotland
      'rue': '🏴󠁧󠁢󠁥󠁮󠁧󠁿',       // England
    };
    return flags[username?.toLowerCase()] || '';
  };
  
  const getLocationName = (username) => {
    const locations = {
      'peter': 'Northern Ireland',
      'edward': 'Scotland',
      'rue': 'England',
    };
    return locations[username?.toLowerCase()] || '';
  };
  
  // Jarvis Voice Assistant State
  const [jarvisOpen, setJarvisOpen] = useState(false);
  const [jarvisListening, setJarvisListening] = useState(false);
  const [jarvisMessage, setJarvisMessage] = useState('');
  const [jarvisResponse, setJarvisResponse] = useState('Hey there! I\'m Jarvis. What can I help you with today?');
  const [jarvisSpeaking, setJarvisSpeaking] = useState(false);
  const [jarvisThinking, setJarvisThinking] = useState(false);
  const [jarvisStreaming, setJarvisStreaming] = useState(false);
  const [jarvisShowMemory, setJarvisShowMemory] = useState(false);
  const [jarvisMemories, setJarvisMemories] = useState([]);
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const [jarvisIdlePrompted, setJarvisIdlePrompted] = useState(false);
  
  // Jarvis face image
  const JARVIS_FACE_URL = "https://static.prod-images.emergentagent.com/jobs/9f5b5584-0ffe-4671-894a-2222cc3f7387/images/821d296d23edff745aed48c9360ff827d084c92d10b88e5f63fb9b4c76ef6f74.jpeg";
  
  // Login form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Profile setup form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profileError, setProfileError] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Clients/Bookings data
  const [bookings, setBookings] = useState([]);
  const [totalBookings, setTotalBookings] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Communications state
  const [adminList, setAdminList] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatPartner, setChatPartner] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  
  // Tasks state
  const [tasks, setTasks] = useState([]);
  const [taskStats, setTaskStats] = useState({ total: 0, pending: 0, in_progress: 0, completed: 0 });
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', assigned_to: '', due_date: '', priority: 'Medium' });
  
  // Notification permission
  const [notificationPermission, setNotificationPermission] = useState('default');
  
  // Admin Management (Super Admin only)
  const [allAdmins, setAllAdmins] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [adminManagementLoading, setAdminManagementLoading] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  
  // 30-Day Sprint state
  const [sprintData, setSprintData] = useState(null);
  const [sprintLoading, setSprintLoading] = useState(false);
  const [expandedStages, setExpandedStages] = useState({});
  const [teamProgress, setTeamProgress] = useState([]);
  const [taskProofImages, setTaskProofImages] = useState({});
  const [uploadingProof, setUploadingProof] = useState(null);
  
  // Group Chat state
  const [chatMode, setChatMode] = useState('direct'); // 'direct' or 'group'
  const [groupMessages, setGroupMessages] = useState([]);
  const [groupNewMessage, setGroupNewMessage] = useState('');
  const [groupLastTimestamp, setGroupLastTimestamp] = useState(null);
  
  const pageSize = 10;

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check for existing session
  useEffect(() => {
    const savedToken = sessionStorage.getItem('adminToken');
    const savedAdmin = sessionStorage.getItem('adminData');
    if (savedToken && savedAdmin) {
      setToken(savedToken);
      setAdminData(JSON.parse(savedAdmin));
      setIsLoggedIn(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      setToken(data.access_token);
      setAdminData({ 
        name: data.name, 
        role: data.role, 
        clearance_level: data.clearance_level,
        authority_level: data.authority_level || 'standard',
        phone: data.phone,
        work_email: data.work_email
      });
      setRequiresProfileSetup(data.requires_profile_setup || false);
      setIsLoggedIn(true);
      
      sessionStorage.setItem('adminToken', data.access_token);
      sessionStorage.setItem('adminData', JSON.stringify({ 
        name: data.name, 
        role: data.role, 
        clearance_level: data.clearance_level,
        authority_level: data.authority_level || 'standard',
        requires_profile_setup: data.requires_profile_setup 
      }));
      
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setToken(null);
    setAdminData(null);
    setBookings([]);
    setRequiresProfileSetup(false);
    setActiveTab('overview');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setProfilePhone('');
    setProfileEmail('');
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminData');
  };

  const handleProfileSetup = async (e) => {
    e.preventDefault();
    setProfileError('');
    
    if (newPassword !== confirmPassword) {
      setProfileError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 4) {
      setProfileError('Password must be at least 4 characters');
      return;
    }
    
    if (!profilePhone || profilePhone.length < 10) {
      setProfileError('Please enter a valid phone number');
      return;
    }
    
    if (!profileEmail || !profileEmail.includes('@')) {
      setProfileError('Please enter a valid work email');
      return;
    }
    
    setProfileLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/setup-profile`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          current_password: currentPassword, 
          new_password: newPassword,
          phone: profilePhone,
          work_email: profileEmail
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Profile setup failed');
      }
      
      setRequiresProfileSetup(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setProfilePhone('');
      setProfileEmail('');
      
      const updatedAdminData = { 
        ...adminData, 
        requires_profile_setup: false,
        phone: data.phone,
        work_email: data.work_email
      };
      setAdminData(updatedAdminData);
      sessionStorage.setItem('adminData', JSON.stringify(updatedAdminData));
      
    } catch (err) {
      setProfileError(err.message);
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchBookings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/bookings?page=${page}&page_size=${pageSize}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          return;
        }
        throw new Error('Failed to fetch bookings');
      }
      
      const data = await response.json();
      setBookings(data.bookings);
      setTotalBookings(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    if (isLoggedIn && token && !requiresProfileSetup) {
      fetchBookings();
    }
  }, [isLoggedIn, token, page, fetchBookings, requiresProfileSetup]);

  const updateStatus = async (clientId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/bookings/${clientId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchBookings();
        if (selectedBooking?.client_id === clientId) {
          setSelectedBooking({ ...selectedBooking, status: newStatus });
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-blue-100 text-blue-700';
      case 'contacted': return 'bg-amber-100 text-amber-700';
      case 'completed': return 'bg-emerald-100 text-emerald-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Communications functions
  const fetchAdminList = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/messages/admins`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminList(data.admins);
      }
    } catch (err) {
      console.error('Error fetching admin list:', err);
    }
  }, [token]);

  const fetchConversations = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/messages/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
    }
  }, [token]);

  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/messages/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, [token]);

  const openChat = async (partnerUsername) => {
    setChatLoading(true);
    setSelectedChat(partnerUsername);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/messages/${partnerUsername}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setChatMessages(data.messages);
        setChatPartner(data.partner);
        fetchUnreadCount();
        fetchConversations();
      }
    } catch (err) {
      console.error('Error opening chat:', err);
    } finally {
      setChatLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat || sendingMessage) return;
    setSendingMessage(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/messages/send`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          recipient_username: selectedChat,
          content: newMessage.trim()
        })
      });
      if (response.ok) {
        const data = await response.json();
        setChatMessages(prev => [...prev, data.message]);
        setNewMessage('');
        fetchConversations();
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  const closeChat = () => {
    setSelectedChat(null);
    setChatMessages([]);
    setChatPartner(null);
  };

  // Fetch communications data when tab is active
  useEffect(() => {
    if (isLoggedIn && token && !requiresProfileSetup && activeTab === 'communications') {
      fetchAdminList();
      fetchConversations();
    }
  }, [isLoggedIn, token, requiresProfileSetup, activeTab, fetchAdminList, fetchConversations]);

  // Poll for new messages when in chat
  useEffect(() => {
    if (!selectedChat || !token) return;
    const interval = setInterval(() => {
      openChat(selectedChat);
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedChat, token]);

  // Fetch unread count periodically
  useEffect(() => {
    if (isLoggedIn && token && !requiresProfileSetup) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, token, requiresProfileSetup, fetchUnreadCount]);

  // Request notification permission
  useEffect(() => {
    if (isLoggedIn && 'Notification' in window) {
      setNotificationPermission(Notification.permission);
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          setNotificationPermission(permission);
        });
      }
    }
  }, [isLoggedIn]);

  // Show notification for new messages
  const showNotification = useCallback((title, body) => {
    if (notificationPermission === 'granted' && document.hidden) {
      new Notification(title, {
        body,
        icon: '/neura-logo.png',
        tag: 'neura-message'
      });
    }
  }, [notificationPermission]);

  // Check for new messages and notify
  useEffect(() => {
    if (chatMessages.length > lastMessageCount && lastMessageCount > 0) {
      const newMsg = chatMessages[chatMessages.length - 1];
      if (newMsg && newMsg.sender_username !== adminData?.name?.toLowerCase()) {
        showNotification(`New message from ${newMsg.sender_name}`, newMsg.content?.substring(0, 50) || 'Sent a file');
      }
    }
    setLastMessageCount(chatMessages.length);
  }, [chatMessages, lastMessageCount, adminData, showNotification]);

  // Task functions
  const fetchTasks = useCallback(async () => {
    if (!token) return;
    setTasksLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTasks(data.tasks);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setTasksLoading(false);
    }
  }, [token]);

  const fetchTaskStats = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/tasks/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTaskStats(data);
      }
    } catch (err) {
      console.error('Error fetching task stats:', err);
    }
  }, [token]);

  const createTask = async () => {
    if (!taskForm.title || !taskForm.assigned_to) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/tasks`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(taskForm)
      });
      if (response.ok) {
        setShowTaskModal(false);
        setTaskForm({ title: '', description: '', assigned_to: '', due_date: '', priority: 'Medium' });
        fetchTasks();
        fetchTaskStats();
      }
    } catch (err) {
      console.error('Error creating task:', err);
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        fetchTasks();
        fetchTaskStats();
      }
    } catch (err) {
      console.error('Error updating task:', err);
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchTasks();
        fetchTaskStats();
      }
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  // File upload for chat
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      alert('File too large (max 10MB)');
      return;
    }
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/messages/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedFile(data);
      } else {
        alert('Failed to upload file');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      alert('Failed to upload file');
    } finally {
      setUploadingFile(false);
      event.target.value = '';
    }
  };

  const sendMessageWithFile = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedChat || sendingMessage) return;
    setSendingMessage(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/messages/send`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          recipient_username: selectedChat,
          content: newMessage.trim() || (selectedFile ? `Shared a file: ${selectedFile.file_name}` : ''),
          file_url: selectedFile?.file_url,
          file_name: selectedFile?.file_name,
          file_type: selectedFile?.file_type
        })
      });
      if (response.ok) {
        const data = await response.json();
        setChatMessages(prev => [...prev, data.message]);
        setNewMessage('');
        setSelectedFile(null);
        fetchConversations();
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  // Admin Management Functions (Super Admin only)
  const fetchAllAdmins = useCallback(async () => {
    if (!token || adminData?.clearance_level !== 'FOUNDER') return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/management/admins`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllAdmins(data.admins);
      }
    } catch (err) {
      console.error('Error fetching admins:', err);
    }
  }, [token, adminData?.clearance_level]);

  const fetchAuditLogs = useCallback(async () => {
    if (!token || adminData?.clearance_level !== 'FOUNDER') return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/management/audit-log?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs);
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    }
  }, [token, adminData?.clearance_level]);

  const updateAdminPermissions = async (username, updates) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/management/admins/${username}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        fetchAllAdmins();
        fetchAuditLogs();
        setEditingAdmin(null);
      }
    } catch (err) {
      console.error('Error updating admin:', err);
    }
  };

  const deactivateAdmin = async (username) => {
    if (!window.confirm(`Are you sure you want to deactivate ${username}?`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/management/admins/${username}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchAllAdmins();
        fetchAuditLogs();
      }
    } catch (err) {
      console.error('Error deactivating admin:', err);
    }
  };

  // 30-Day Sprint Functions
  const fetchSprintData = useCallback(async () => {
    if (!token) return;
    setSprintLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/sprint/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSprintData(data);
        // Expand all stages by default
        const expanded = {};
        data.stages.forEach(stage => { expanded[stage.key] = true; });
        setExpandedStages(expanded);
      }
    } catch (err) {
      console.error('Error fetching sprint data:', err);
    } finally {
      setSprintLoading(false);
    }
  }, [token]);

  const fetchTeamProgress = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/sprint/team-progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeamProgress(data.team_progress);
      }
    } catch (err) {
      console.error('Error fetching team progress:', err);
    }
  }, [token]);

  const toggleSprintTask = async (taskId, completed, proofImages = []) => {
    try {
      // If completing task, need 2 proof images
      if (completed && proofImages.length < 2) {
        alert('Please upload 2 proof images before marking this task as complete');
        return false;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/admin/sprint/toggle-task`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          task_id: taskId, 
          completed,
          proof_images: completed ? proofImages : null
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        alert(error.detail || 'Failed to complete task');
        return false;
      }
      
      // Clear proof images for this task after successful completion
      if (completed) {
        setTaskProofImages(prev => ({ ...prev, [taskId]: [] }));
      }
      
      // Refresh for accurate counts
      fetchSprintData();
      return true;
    } catch (err) {
      console.error('Error toggling task:', err);
      return false;
    }
  };

  const handleProofImageUpload = async (taskId, files) => {
    if (!files || files.length === 0) return;
    
    setUploadingProof(taskId);
    const uploadedUrls = [];
    
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${BACKEND_URL}/api/upload/file`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          uploadedUrls.push(data.url);
        }
      }
      
      setTaskProofImages(prev => ({
        ...prev,
        [taskId]: [...(prev[taskId] || []), ...uploadedUrls]
      }));
    } catch (err) {
      console.error('Error uploading proof images:', err);
    } finally {
      setUploadingProof(null);
    }
  };

  const removeProofImage = (taskId, index) => {
    setTaskProofImages(prev => ({
      ...prev,
      [taskId]: prev[taskId]?.filter((_, i) => i !== index) || []
    }));
  };

  // Group Chat Functions
  const fetchGroupMessages = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/messages/group/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGroupMessages(data.messages);
        if (data.messages.length > 0) {
          setGroupLastTimestamp(data.messages[data.messages.length - 1].created_at);
        }
      }
    } catch (err) {
      console.error('Error fetching group messages:', err);
    }
  }, [token]);

  const sendGroupMessage = async () => {
    if (!groupNewMessage.trim() && !selectedFile) return;
    
    setSendingMessage(true);
    try {
      let fileData = {};
      
      // Upload file if selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        
        const uploadResponse = await fetch(`${BACKEND_URL}/api/upload/file`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          fileData = {
            file_url: uploadData.url,
            file_name: selectedFile.name,
            file_type: selectedFile.type
          };
        }
      }
      
      const response = await fetch(`${BACKEND_URL}/api/admin/messages/group/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: groupNewMessage,
          ...fileData
        })
      });
      
      if (response.ok) {
        setGroupNewMessage('');
        setSelectedFile(null);
        fetchGroupMessages();
      }
    } catch (err) {
      console.error('Error sending group message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  const toggleStageExpanded = (stageKey) => {
    setExpandedStages(prev => ({ ...prev, [stageKey]: !prev[stageKey] }));
  };

  // Jarvis Voice Assistant Functions
  const startJarvisListening = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setJarvisResponse('Sorry, voice recognition is not supported in your browser. Please use Chrome or Edge.');
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-GB';
    recognition.continuous = false;
    recognition.interimResults = false;
    
    setJarvisListening(true);
    setJarvisResponse('Listening...');
    
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setJarvisMessage(transcript);
      handleJarvisCommand(transcript);
    };
    
    recognition.onerror = (event) => {
      setJarvisListening(false);
      setJarvisResponse('Sorry, I couldn\'t hear that. Please try again.');
    };
    
    recognition.onend = () => {
      setJarvisListening(false);
    };
    
    recognition.start();
  };

  const handleJarvisCommand = async (command) => {
    const lowerCommand = command.toLowerCase();
    
    // Handle quick navigation commands locally first
    let navigated = false;
    if (lowerCommand.includes('overview') || lowerCommand.includes('dashboard')) {
      setActiveTab('overview');
      navigated = true;
    } else if (lowerCommand.includes('sprint') || (lowerCommand.includes('tasks') && lowerCommand.includes('30'))) {
      setActiveTab('sprint');
      navigated = true;
    } else if (lowerCommand.includes('client') && !lowerCommand.includes('find') && !lowerCommand.includes('how')) {
      setActiveTab('clients');
      navigated = true;
    } else if (lowerCommand.includes('project')) {
      setActiveTab('projects');
      navigated = true;
    } else if (lowerCommand.includes('task') && !lowerCommand.includes('30')) {
      setActiveTab('tasks');
      navigated = true;
    } else if (lowerCommand.includes('communication') || lowerCommand.includes('team chat')) {
      setActiveTab('communications');
      navigated = true;
    } else if (lowerCommand.includes('analytic') || lowerCommand.includes('stats')) {
      setActiveTab('analytics');
      navigated = true;
    } else if (lowerCommand.includes('billing') || lowerCommand.includes('invoice')) {
      setActiveTab('billing');
      navigated = true;
    } else if (lowerCommand.includes('setting')) {
      setActiveTab('settings');
      navigated = true;
    } else if (lowerCommand.includes('log out') || lowerCommand.includes('logout') || lowerCommand.includes('sign out')) {
      setJarvisResponse('Logging you out. Goodbye!');
      speakJarvisResponse('Logging you out. Goodbye!');
      setTimeout(() => handleLogout(), 2000);
      return;
    } else if (lowerCommand.includes('google maps') || lowerCommand.includes('find leads on map')) {
      window.open('https://www.google.com/maps/search/construction+companies+UK', '_blank');
      setJarvisResponse('Opening Google Maps to find construction company leads.');
      speakJarvisResponse('Opening Google Maps to find construction company leads.');
      return;
    }
    
    // For navigation commands, still send to AI for a nice response
    if (navigated) {
      setJarvisResponse('Navigating...');
    } else {
      setJarvisResponse('');
    }
    
    setJarvisThinking(true);
    setJarvisStreaming(true);
    
    // Send to AI for intelligent streaming response
    try {
      const response = await fetch(`${BACKEND_URL}/api/jarvis/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: command })
      });
      
      if (response.ok) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullResponse = '';
        
        setJarvisThinking(false);
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                setJarvisStreaming(false);
                speakJarvisResponse(fullResponse);
                break;
              }
              fullResponse += data;
              setJarvisResponse(fullResponse);
            }
          }
        }
      } else {
        setJarvisThinking(false);
        setJarvisStreaming(false);
        const fallback = getFallbackResponse(command);
        setJarvisResponse(fallback);
        speakJarvisResponse(fallback);
      }
    } catch (err) {
      console.error('Jarvis AI error:', err);
      setJarvisThinking(false);
      setJarvisStreaming(false);
      const fallback = getFallbackResponse(command);
      setJarvisResponse(fallback);
      speakJarvisResponse(fallback);
    }
  };

  const getFallbackResponse = (command) => {
    const lowerCommand = command.toLowerCase();
    if (lowerCommand.includes('hello') || lowerCommand.includes('hi')) {
      return `Hello ${adminData?.name}! I'm Jarvis. How can I help you today?`;
    }
    if (lowerCommand.includes('help')) {
      return 'I can help with lead generation strategies, sales outreach tips, CRM best practices, and navigating this dashboard. Just ask!';
    }
    if (lowerCommand.includes('time')) {
      return `The current time is ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}.`;
    }
    return `I heard "${command}". I'm having trouble connecting to my AI brain right now, but I can still help you navigate. Try saying "overview", "tasks", "sprint", or "find leads".`;
  };

  const speakJarvisResponse = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-GB';
      utterance.rate = 1.15;  // Faster, natural human speaking pace
      utterance.pitch = 0.9;  // Slightly lower pitch for male voice
      utterance.volume = 1;
      
      // Find the best male voice - prioritize Google/Microsoft high-quality voices
      const voices = window.speechSynthesis.getVoices();
      const maleVoice = 
        voices.find(v => v.name.includes('Google UK English Male')) ||
        voices.find(v => v.name.includes('Microsoft George')) ||
        voices.find(v => v.name.includes('Daniel') && v.lang.startsWith('en')) ||
        voices.find(v => v.name.includes('James') && v.lang.startsWith('en')) ||
        voices.find(v => v.name.toLowerCase().includes('male') && v.lang.startsWith('en-GB')) ||
        voices.find(v => v.lang === 'en-GB' && !v.name.toLowerCase().includes('female')) ||
        voices.find(v => v.lang === 'en-GB') ||
        voices.find(v => v.lang.startsWith('en') && !v.name.toLowerCase().includes('female'));
      if (maleVoice) utterance.voice = maleVoice;
      
      setJarvisSpeaking(true);
      utterance.onend = () => setJarvisSpeaking(false);
      window.speechSynthesis.speak(utterance);
    }
  };

  const sendJarvisTextMessage = async () => {
    if (jarvisMessage.trim()) {
      const message = jarvisMessage;
      setJarvisMessage('');
      await handleJarvisCommand(message);
    }
  };

  // Fetch Jarvis memories
  const fetchJarvisMemories = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/jarvis/memories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setJarvisMemories(data.memories || []);
      }
    } catch (err) {
      console.error('Error fetching Jarvis memories:', err);
    }
  }, [token]);

  const clearJarvisMemories = async () => {
    if (!window.confirm('Are you sure you want to clear all of Jarvis\'s memories about you?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/jarvis/memories`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setJarvisMemories([]);
        setJarvisResponse('My memories have been cleared. We can start fresh!');
      }
    } catch (err) {
      console.error('Error clearing memories:', err);
    }
  };

  // Fetch memories when Jarvis opens
  useEffect(() => {
    if (jarvisOpen && token) {
      fetchJarvisMemories();
    }
  }, [jarvisOpen, token, fetchJarvisMemories]);

  // Idle detection - Jarvis speaks after 1 minute of inactivity
  useEffect(() => {
    if (!isLoggedIn || !token || requiresProfileSetup) return;
    
    const idleMessages = [
      "Hey, everything alright? Let me know if you need anything.",
      "Still here if you need me, mate.",
      "Taking a break? I'm here when you're ready.",
      "Need any help with your leads or outreach?",
      "Quick tip - have you checked your sprint tasks today?",
      "Fancy a chat about your prospects? I'm all ears.",
      "Don't forget to follow up with your warm leads!",
    ];
    
    const handleActivity = () => {
      setLastActivityTime(Date.now());
      setJarvisIdlePrompted(false);
    };
    
    // Track user activity
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('scroll', handleActivity);
    
    // Check for idle every 30 seconds
    const idleInterval = setInterval(() => {
      const idleTime = Date.now() - lastActivityTime;
      if (idleTime > 60000 && !jarvisIdlePrompted && !jarvisOpen) { // 1 minute
        const randomMessage = idleMessages[Math.floor(Math.random() * idleMessages.length)];
        setJarvisResponse(randomMessage);
        setJarvisOpen(true);
        setJarvisIdlePrompted(true);
        speakJarvisResponse(randomMessage);
      }
    }, 30000);
    
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      clearInterval(idleInterval);
    };
  }, [isLoggedIn, token, requiresProfileSetup, lastActivityTime, jarvisIdlePrompted, jarvisOpen]);

  // Fetch admin management data when settings tab is active (Super Admin only)
  useEffect(() => {
    if (isLoggedIn && token && !requiresProfileSetup && activeTab === 'settings' && adminData?.clearance_level === 'FOUNDER') {
      setAdminManagementLoading(true);
      Promise.all([fetchAllAdmins(), fetchAuditLogs()]).finally(() => setAdminManagementLoading(false));
    }
  }, [isLoggedIn, token, requiresProfileSetup, activeTab, adminData?.clearance_level, fetchAllAdmins, fetchAuditLogs]);

  // Fetch tasks when tab is active
  useEffect(() => {
    if (isLoggedIn && token && !requiresProfileSetup && activeTab === 'tasks') {
      fetchTasks();
      fetchTaskStats();
      fetchAdminList();
    }
  }, [isLoggedIn, token, requiresProfileSetup, activeTab, fetchTasks, fetchTaskStats, fetchAdminList]);

  // Fetch sprint data when tab is active
  useEffect(() => {
    if (isLoggedIn && token && !requiresProfileSetup && activeTab === 'sprint') {
      fetchSprintData();
      fetchTeamProgress();
    }
  }, [isLoggedIn, token, requiresProfileSetup, activeTab, fetchSprintData, fetchTeamProgress]);

  const filteredBookings = bookings.filter(b => 
    b.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.client_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(totalBookings / pageSize);

  // Navigation items
  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'sprint', label: '30-Day Sprint', icon: Rocket },
    { id: 'clients', label: 'Clients', icon: Users },
    { id: 'projects', label: 'Projects', icon: FolderKanban },
    { id: 'tasks', label: 'Tasks', icon: ListTodo },
    { id: 'activity', label: 'Activity', icon: Activity },
    { id: 'communications', label: 'Communications', icon: MessageSquare },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  if (!isOpen) return null;

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="fixed inset-0 z-[200] overflow-auto">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-600 via-blue-300 via-30% to-white">
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        
        <header className="relative z-10 bg-white shadow-md border-b border-gray-100 px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-3">
              <img src="/neura-logo.png" alt="NEURA AI" className="h-8 sm:h-10 w-auto" />
              <div className="flex flex-col">
                <span className="text-base sm:text-lg font-bold tracking-wide">
                  <span className="bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">NEURA</span>
                  <span className="text-gray-800"> AI</span>
                </span>
                <span className="text-[8px] text-gray-500 tracking-[0.15em] font-medium">MANAGEMENT HUB</span>
              </div>
            </div>
            <Button onClick={onClose} variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700" data-testid="close-admin-btn">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </header>

        <main className="relative z-10 flex items-center justify-center min-h-[calc(100vh-80px)] p-4">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-500 px-6 py-6 sm:py-8 text-center">
                <img src="/neura-logo.png" alt="NEURA AI" className="h-12 sm:h-16 w-auto mx-auto mb-3 sm:mb-4" />
                <h2 className="text-xl sm:text-2xl font-bold text-white">Management HUB</h2>
                <p className="text-blue-100 text-sm mt-1 sm:mt-2">NEURA AI SOFTWARES</p>
              </div>
              
              <form onSubmit={handleLogin} className="p-4 sm:p-6 space-y-4" data-testid="admin-login-form">
                {loginError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{loginError}</span>
                  </div>
                )}
                
                <div>
                  <label className="block text-gray-600 text-sm mb-2 font-medium">Username</label>
                  <Input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username"
                    className="bg-gray-50 border-gray-200 text-gray-800"
                    required
                    data-testid="admin-username-input"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-600 text-sm mb-2 font-medium">Password</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="bg-gray-50 border-gray-200 text-gray-800"
                    required
                    data-testid="admin-password-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-lg"
                  disabled={loginLoading}
                  data-testid="admin-login-btn"
                >
                  {loginLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                  Sign In
                </Button>
              </form>
              
              <div className="px-6 pb-4 sm:pb-6 text-center">
                <p className="text-gray-400 text-xs">Authorized personnel only</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Profile Setup Screen (First Login)
  if (requiresProfileSetup) {
    return (
      <div className="fixed inset-0 z-[200] overflow-auto">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-600 via-blue-300 via-30% to-white">
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        
        <header className="relative z-10 bg-white shadow-md border-b border-gray-100 px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-3">
              <img src="/neura-logo.png" alt="NEURA AI" className="h-8 sm:h-10 w-auto" />
              <div className="flex flex-col">
                <span className="text-base sm:text-lg font-bold tracking-wide">
                  <span className="bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">NEURA</span>
                  <span className="text-gray-800"> AI</span>
                </span>
                <span className="text-[8px] text-gray-500 tracking-[0.15em] font-medium">MANAGEMENT HUB</span>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="text-sm text-gray-600 hidden sm:inline">{adminData?.name}</span>
              <Button onClick={handleLogout} variant="ghost" size="sm" className="text-gray-500 hover:text-red-600">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        <main className="relative z-10 flex items-center justify-center min-h-[calc(100vh-80px)] p-4">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-6 sm:py-8 text-center">
                <Shield className="w-12 sm:w-16 h-12 sm:h-16 text-white mx-auto mb-3 sm:mb-4" />
                <h2 className="text-xl sm:text-2xl font-bold text-white">Complete Your Profile</h2>
                <p className="text-blue-100 text-sm mt-1 sm:mt-2">Required for first login</p>
              </div>
              
              <form onSubmit={handleProfileSetup} className="p-4 sm:p-6 space-y-4" data-testid="profile-setup-form">
                {profileError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{profileError}</span>
                  </div>
                )}
                
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
                  Welcome, <span className="font-semibold">{adminData?.name}</span>! Please complete your profile to continue.
                  {adminData?.authority_level === 'high' && (
                    <span className="block mt-1 text-xs text-blue-600 font-medium">You have HIGH AUTHORITY access.</span>
                  )}
                </div>
                
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Security</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-gray-600 text-sm mb-1.5 font-medium">Current Password</label>
                      <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter your current password" className="bg-gray-50 border-gray-200" required data-testid="current-password-input" />
                    </div>
                    
                    <div>
                      <label className="block text-gray-600 text-sm mb-1.5 font-medium">New Password</label>
                      <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Min 4 characters" className="bg-gray-50 border-gray-200" required minLength={4} data-testid="new-password-input" />
                    </div>
                    
                    <div>
                      <label className="block text-gray-600 text-sm mb-1.5 font-medium">Confirm New Password</label>
                      <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password" className="bg-gray-50 border-gray-200" required data-testid="confirm-password-input" />
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Contact Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-gray-600 text-sm mb-1.5 font-medium">Phone Number</label>
                      <Input type="tel" value={profilePhone} onChange={(e) => setProfilePhone(e.target.value)}
                        placeholder="+44 7XXX XXXXXX" className="bg-gray-50 border-gray-200" required data-testid="profile-phone-input" />
                    </div>
                    
                    <div>
                      <label className="block text-gray-600 text-sm mb-1.5 font-medium">Work Email</label>
                      <Input type="email" value={profileEmail} onChange={(e) => setProfileEmail(e.target.value)}
                        placeholder="you@company.com" className="bg-gray-50 border-gray-200" required data-testid="profile-email-input" />
                    </div>
                  </div>
                </div>
                
                <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white shadow-lg"
                  disabled={profileLoading} data-testid="complete-profile-btn">
                  {profileLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                  Complete Profile & Continue
                </Button>
              </form>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Main Dashboard with Sidebar
  return (
    <div className="fixed inset-0 z-[200] flex flex-col bg-gray-50">
      {/* Top Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 flex-shrink-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="md:hidden text-gray-600" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="toggle-sidebar">
              <Menu className="w-5 h-5" />
            </Button>
            <img src="/neura-logo.png" alt="NEURA AI" className="h-8 w-auto" />
            <div className="hidden sm:flex flex-col">
              <span className="text-sm font-bold">
                <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">NEURA</span>
                <span className="text-gray-800"> AI</span>
              </span>
              <span className="text-[8px] text-gray-500 tracking-wider">MANAGEMENT HUB</span>
            </div>
            {/* Google Maps Link for Lead Generation */}
            <a 
              href="https://www.google.com/maps/search/construction+companies+UK" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all text-sm font-medium shadow-sm"
              data-testid="google-maps-leads-btn"
            >
              <MapPin className="w-4 h-4" />
              Find Leads on Maps
            </a>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Mobile Maps Button */}
            <a 
              href="https://www.google.com/maps/search/construction+companies+UK" 
              target="_blank" 
              rel="noopener noreferrer"
              className="md:hidden flex items-center justify-center w-9 h-9 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg"
              data-testid="google-maps-leads-btn-mobile"
            >
              <MapPin className="w-4 h-4" />
            </a>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-gray-500 hover:text-gray-700 relative"
              onClick={() => setActiveTab('communications')}
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-800">
                  {getLocationFlag(adminData?.username)} {adminData?.name}
                </p>
                <p className="text-xs text-blue-600">{adminData?.role}</p>
              </div>
            </div>
            <Button onClick={handleLogout} variant="ghost" size="sm" className="text-gray-500 hover:text-red-600" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
            <Button onClick={onClose} variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700" data-testid="close-admin-btn">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 fixed md:relative z-30 md:z-auto w-64 h-full bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out flex flex-col`}>
          {/* Sidebar Header - Mobile */}
          <div className="md:hidden flex items-center justify-between p-4 border-b border-gray-100">
            <span className="font-semibold text-gray-800">Menu</span>
            <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-3 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); if (isMobile) setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                    isActive 
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/30' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>
          
          {/* Sidebar Footer */}
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                {adminData?.name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">
                  {getLocationFlag(adminData?.username)} {adminData?.name}
                </p>
                <p className="text-xs text-blue-600">{adminData?.role} • {getLocationName(adminData?.username)}</p>
              </div>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && isMobile && (
          <div className="fixed inset-0 bg-black/50 z-20 md:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6 max-w-7xl mx-auto">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-800">Welcome back, {getLocationFlag(adminData?.username)} {adminData?.name}!</h1>
                    <p className="text-gray-500">Here's what's happening with your business today.</p>
                  </div>
                  <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg w-full sm:w-auto">
                    <Plus className="w-4 h-4 mr-2" /> New Client
                  </Button>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                        <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500">Total Clients</p>
                        <p className="text-xl sm:text-2xl font-bold text-gray-800">{totalBookings}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                        <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500">Revenue</p>
                        <p className="text-xl sm:text-2xl font-bold text-gray-800">£12.4k</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                        <FolderKanban className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500">Active Projects</p>
                        <p className="text-xl sm:text-2xl font-bold text-gray-800">8</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                        <ListTodo className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500">Pending Tasks</p>
                        <p className="text-xl sm:text-2xl font-bold text-gray-800">14</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-800">Recent Clients</h3>
                    <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => setActiveTab('clients')}>View All</Button>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {bookings.slice(0, 5).map((booking) => (
                      <div key={booking.client_id} className="px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 sm:gap-4 hover:bg-gray-50 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                          {booking.name?.charAt(0) || 'C'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-800 truncate">{booking.name}</p>
                          <p className="text-sm text-gray-500 truncate">{booking.email}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                      </div>
                    ))}
                    {bookings.length === 0 && (
                      <div className="px-6 py-8 text-center text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No clients yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* 30-Day Sprint Tab */}
            {activeTab === 'sprint' && (
              <div className="space-y-6">
                {sprintLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                  </div>
                ) : sprintData ? (
                  <>
                    {/* Sprint Header */}
                    <div className="bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-500 rounded-2xl p-6 text-white">
                      <div className="flex items-center gap-3 mb-2">
                        <Rocket className="w-8 h-8" />
                        <h1 className="text-2xl font-bold">{sprintData.info.title}</h1>
                      </div>
                      <p className="text-blue-100 mb-4">{sprintData.info.objective}</p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div className="bg-white/10 rounded-xl p-3">
                          <p className="text-xs text-blue-200">Target Market</p>
                          <p className="font-semibold">{sprintData.info.target_market}</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-3">
                          <p className="text-xs text-blue-200">Working Hours</p>
                          <p className="font-semibold text-sm">{sprintData.info.working_hours}</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-3">
                          <p className="text-xs text-blue-200">Core Offer</p>
                          <p className="font-semibold text-sm">{sprintData.info.core_offer}</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-3">
                          <p className="text-xs text-blue-200">Overall Progress</p>
                          <p className="font-bold text-xl">{sprintData.overall_progress.percent}%</p>
                        </div>
                      </div>
                    </div>

                    {/* Overall Progress Bar */}
                    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-700">Overall Progress</span>
                        <span className="text-sm text-gray-500">{sprintData.overall_progress.completed} / {sprintData.overall_progress.total} tasks</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-4">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-cyan-500 h-4 rounded-full transition-all duration-500"
                          style={{ width: `${sprintData.overall_progress.percent}%` }}
                        />
                      </div>
                    </div>

                    {/* Team Progress (for Founder) */}
                    {adminData?.clearance_level === 'FOUNDER' && teamProgress.length > 0 && (
                      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                          <Users className="w-5 h-5 text-blue-500" />
                          Team Progress
                        </h3>
                        <div className="space-y-3">
                          {teamProgress.map(member => (
                            <div key={member.username} className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                                {member.name.charAt(0)}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-gray-800">{getLocationFlag(member.username)} {member.name}</span>
                                  <span className="text-sm text-gray-500">{member.percent}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                                    style={{ width: `${member.percent}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Team Rule */}
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                      <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        TEAM RULE
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-xs text-red-600 font-semibold mb-1">WE DO NOT SELL:</p>
                          <p className="text-red-700">"{sprintData.info.team_rule.dont_sell}"</p>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <p className="text-xs text-green-600 font-semibold mb-1">WE SELL:</p>
                          <p className="text-green-700 text-sm">"{sprintData.info.team_rule.do_sell}"</p>
                        </div>
                      </div>
                    </div>

                    {/* 30-Day Targets */}
                    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Target className="w-5 h-5 text-blue-500" />
                        30-DAY TARGETS
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {Object.entries(sprintData.info.targets).map(([key, value]) => (
                          <div key={key} className="bg-gray-50 rounded-lg p-3 text-center">
                            <p className="text-sm text-gray-600">{value}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Daily Routine */}
                    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-blue-500" />
                        DAILY TEAM ROUTINE
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.values(sprintData.info.daily_routine).map((slot, idx) => (
                          <div key={idx} className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                            <p className="text-xs text-blue-600 font-semibold">{slot.time}</p>
                            <p className="text-sm text-gray-700 mt-1">{slot.activity}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Stages */}
                    <div className="space-y-4">
                      <h2 className="text-xl font-bold text-gray-800">Task Phases</h2>
                      {sprintData.stages.map(stage => (
                        <div key={stage.key} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                          {/* Stage Header */}
                          <button
                            onClick={() => toggleStageExpanded(stage.key)}
                            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white hover:from-blue-50 hover:to-white transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                stage.progress_percent === 100 ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                              }`}>
                                {stage.progress_percent === 100 ? <CheckCircle2 className="w-5 h-5" /> : <Rocket className="w-5 h-5" />}
                              </div>
                              <div className="text-left">
                                <h3 className="font-semibold text-gray-800">{stage.title}</h3>
                                <p className="text-xs text-gray-500">{stage.days} • {stage.completed_count}/{stage.total_count} tasks</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="w-24 bg-gray-200 rounded-full h-2 hidden sm:block">
                                <div 
                                  className={`h-2 rounded-full transition-all duration-300 ${
                                    stage.progress_percent === 100 ? 'bg-green-500' : 'bg-blue-500'
                                  }`}
                                  style={{ width: `${stage.progress_percent}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium text-gray-600 w-12 text-right">{stage.progress_percent}%</span>
                              {expandedStages[stage.key] ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                            </div>
                          </button>

                          {/* Stage Tasks */}
                          {expandedStages[stage.key] && (
                            <div className="border-t border-gray-100">
                              <div className="p-4 space-y-3">
                                {stage.daily_target && (
                                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                                    <p className="text-sm text-amber-700 font-medium">Daily Target: {stage.daily_target}</p>
                                  </div>
                                )}
                                {stage.tasks.map(task => (
                                  <div 
                                    key={task.id}
                                    className={`p-3 rounded-lg transition-colors ${
                                      task.completed ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
                                    }`}
                                  >
                                    <div className="flex items-start gap-3">
                                      <span className={`flex-1 text-sm ${task.completed ? 'text-green-700 line-through' : 'text-gray-700'}`}>
                                        {task.text}
                                      </span>
                                      {task.completed && (
                                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                                      )}
                                    </div>
                                    
                                    {/* Image Upload Section - Only show if not completed */}
                                    {!task.completed && (
                                      <div className="mt-3 pt-3 border-t border-gray-200">
                                        <p className="text-xs text-gray-500 mb-2">Upload 2 proof images to complete this task:</p>
                                        
                                        {/* Current proof images */}
                                        {taskProofImages[task.id]?.length > 0 && (
                                          <div className="flex flex-wrap gap-2 mb-2">
                                            {taskProofImages[task.id].map((url, idx) => (
                                              <div key={idx} className="relative w-16 h-16">
                                                <img src={url} alt={`Proof ${idx + 1}`} className="w-full h-full object-cover rounded-lg border border-gray-200" />
                                                <button
                                                  onClick={() => removeProofImage(task.id, idx)}
                                                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                                                >
                                                  <X className="w-3 h-3" />
                                                </button>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        
                                        <div className="flex items-center gap-2">
                                          <label className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg cursor-pointer hover:bg-blue-100 text-sm">
                                            <Image className="w-4 h-4" />
                                            {uploadingProof === task.id ? 'Uploading...' : 'Upload Image'}
                                            <input
                                              type="file"
                                              accept="image/*"
                                              multiple
                                              className="hidden"
                                              onChange={(e) => handleProofImageUpload(task.id, Array.from(e.target.files))}
                                              disabled={uploadingProof === task.id}
                                            />
                                          </label>
                                          
                                          <span className="text-xs text-gray-500">
                                            {taskProofImages[task.id]?.length || 0}/2 images
                                          </span>
                                          
                                          {(taskProofImages[task.id]?.length || 0) >= 2 && (
                                            <Button
                                              size="sm"
                                              className="bg-green-500 hover:bg-green-600 text-white"
                                              onClick={() => toggleSprintTask(task.id, true, taskProofImages[task.id])}
                                              data-testid={`complete-task-${task.id}`}
                                            >
                                              <CheckCircle2 className="w-4 h-4 mr-1" />
                                              Complete Task
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Uncomplete button for completed tasks */}
                                    {task.completed && (
                                      <div className="mt-2 pt-2 border-t border-green-200">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="text-gray-500 hover:text-red-600 text-xs"
                                          onClick={() => toggleSprintTask(task.id, false, [])}
                                        >
                                          Mark as Incomplete
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                              <div className="bg-gray-50 px-4 py-3 border-t border-gray-100">
                                <p className="text-xs text-gray-500">
                                  <span className="font-semibold">Deliverable:</span> {stage.deliverable}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Demo Flow */}
                    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                      <h3 className="font-semibold text-gray-800 mb-4">DEMONSTRATION FLOW</h3>
                      <div className="flex flex-wrap items-center gap-2">
                        {sprintData.info.demo_flow.map((step, idx) => (
                          <React.Fragment key={idx}>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-700">
                              {step}
                            </div>
                            {idx < sprintData.info.demo_flow.length - 1 && (
                              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-20 text-gray-500">
                    Failed to load sprint data. Please refresh.
                  </div>
                )}
              </div>
            )}

            {/* Clients Tab */}
            {activeTab === 'clients' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <h1 className="text-2xl font-bold text-gray-800">Clients</h1>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1 sm:w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search clients..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-white"
                        data-testid="search-input"
                      />
                    </div>
                    <Button onClick={fetchBookings} variant="outline" className="border-gray-200" data-testid="refresh-btn">
                      <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                  </div>
                </div>

                {/* Mobile Cards View */}
                <div className="md:hidden space-y-4">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                    </div>
                  ) : filteredBookings.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No clients found</p>
                    </div>
                  ) : (
                    filteredBookings.map((booking) => (
                      <div key={booking.client_id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4" onClick={() => setSelectedBooking(booking)}>
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                            {booking.name?.charAt(0) || 'C'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-800">{booking.name}</p>
                            <p className="text-sm text-gray-500 truncate">{booking.company_name || 'Individual'}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(booking.status)}`}>
                            {booking.status}
                          </span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="truncate">{booking.email}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span>{booking.phone || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>{booking.preferred_date || 'TBD'} {booking.preferred_time || ''}</span>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                          <Button size="sm" variant="outline" className="flex-1" onClick={(e) => { e.stopPropagation(); window.location.href = `tel:${booking.phone}`; }}>
                            <Phone className="w-4 h-4 mr-1" /> Call
                          </Button>
                          <Button size="sm" variant="outline" className="flex-1" onClick={(e) => { e.stopPropagation(); window.location.href = `mailto:${booking.email}`; }}>
                            <Mail className="w-4 h-4 mr-1" /> Email
                          </Button>
                          <Button size="sm" className="bg-blue-500 text-white" onClick={(e) => { e.stopPropagation(); setSelectedBooking(booking); }}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Client</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Booking</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {loading ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-12 text-center">
                              <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto" />
                            </td>
                          </tr>
                        ) : filteredBookings.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                              <p>No clients found</p>
                            </td>
                          </tr>
                        ) : filteredBookings.map((booking) => (
                          <tr key={booking.client_id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedBooking(booking)}>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                                  {booking.name?.charAt(0) || 'C'}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-800">{booking.name}</p>
                                  <p className="text-sm text-gray-500">{booking.company_name || 'Individual'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm text-gray-700">{booking.email}</p>
                              <p className="text-sm text-gray-500">{booking.phone || 'N/A'}</p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm text-gray-700">{booking.preferred_date || 'TBD'}</p>
                              <p className="text-sm text-gray-500">{booking.preferred_time || 'TBD'}</p>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(booking.status)}`}>
                                {booking.status}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <Button variant="ghost" size="sm" className="text-blue-600" onClick={(e) => { e.stopPropagation(); setSelectedBooking(booking); }}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                      <p className="text-sm text-gray-500">
                        Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalBookings)} of {totalBookings}
                      </p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="flex items-center px-3 text-sm text-gray-500">Page {page} of {totalPages}</span>
                        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Projects Tab */}
            {activeTab === 'projects' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <h1 className="text-2xl font-bold text-gray-800">Projects</h1>
                  <Button className="bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg w-full sm:w-auto">
                    <Plus className="w-4 h-4 mr-2" /> New Project
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { name: 'Website Redesign', client: 'Anderson Legal', progress: 75, status: 'In Progress' },
                    { name: 'E-commerce Platform', client: 'Patel Construction', progress: 40, status: 'In Progress' },
                    { name: 'CRM Integration', client: 'Belfast Dental', progress: 100, status: 'Completed' },
                    { name: 'AI Chatbot', client: 'Tech Solutions UK', progress: 20, status: 'Planning' },
                  ].map((project, idx) => (
                    <div key={idx} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-gray-800">{project.name}</h3>
                          <p className="text-sm text-gray-500">{project.client}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          project.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' :
                          project.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>{project.status}</span>
                      </div>
                      <div className="mb-2">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-500">Progress</span>
                          <span className="font-medium text-gray-800">{project.progress}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full" style={{ width: `${project.progress}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <h1 className="text-2xl font-bold text-gray-800">Tasks</h1>
                  <Button 
                    onClick={() => { setEditingTask(null); setTaskForm({ title: '', description: '', assigned_to: '', due_date: '', priority: 'Medium' }); setShowTaskModal(true); }}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg w-full sm:w-auto"
                    data-testid="add-task-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Task
                  </Button>
                </div>

                {/* Task Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <p className="text-sm text-gray-500">Total</p>
                    <p className="text-2xl font-bold text-gray-800">{taskStats.total}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <p className="text-sm text-gray-500">Pending</p>
                    <p className="text-2xl font-bold text-amber-600">{taskStats.pending}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <p className="text-sm text-gray-500">In Progress</p>
                    <p className="text-2xl font-bold text-blue-600">{taskStats.in_progress}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <p className="text-sm text-gray-500">Completed</p>
                    <p className="text-2xl font-bold text-emerald-600">{taskStats.completed}</p>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-4 sm:px-6 py-3 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-800">All Tasks</h3>
                    <Button variant="ghost" size="sm" onClick={() => { fetchTasks(); fetchTaskStats(); }} data-testid="refresh-tasks-btn">
                      <RefreshCw className={`w-4 h-4 ${tasksLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  
                  {tasksLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                    </div>
                  ) : tasks.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500">
                      <ListTodo className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No tasks yet</p>
                      <p className="text-sm">Create a task to get started</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {tasks.map((task) => (
                        <div key={task.task_id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-gray-50">
                          <div className="flex items-center gap-3 flex-1">
                            <input 
                              type="checkbox" 
                              checked={task.status === 'completed'}
                              onChange={() => updateTaskStatus(task.task_id, task.status === 'completed' ? 'pending' : 'completed')}
                              className="w-5 h-5 rounded border-gray-300 text-blue-500 focus:ring-blue-500" 
                              data-testid={`task-checkbox-${task.task_id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium ${task.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{task.title}</p>
                              <p className="text-sm text-gray-500 truncate">{task.description || 'No description'}</p>
                              <p className="text-xs text-gray-400 mt-1">Assigned to: {task.assigned_to_name} • By: {task.assigned_by_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-3 ml-8 sm:ml-0">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              task.priority === 'High' ? 'bg-red-100 text-red-700' :
                              task.priority === 'Medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>{task.priority}</span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              task.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                              task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>{task.status}</span>
                            {task.due_date && (
                              <span className="text-sm text-gray-500">{task.due_date}</span>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => deleteTask(task.task_id)}
                              data-testid={`delete-task-${task.task_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Task Create Modal */}
                {showTaskModal && (
                  <div className="fixed inset-0 z-[260] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-500 to-orange-500">
                        <h3 className="text-white font-semibold text-lg">{editingTask ? 'Edit Task' : 'Create Task'}</h3>
                        <Button variant="ghost" size="sm" onClick={() => setShowTaskModal(false)} className="text-white hover:bg-white/20">
                          <X className="w-5 h-5" />
                        </Button>
                      </div>
                      
                      <div className="p-6 space-y-4">
                        <div>
                          <label className="block text-gray-600 text-sm mb-2 font-medium">Task Title *</label>
                          <Input
                            type="text"
                            value={taskForm.title}
                            onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                            placeholder="Enter task title"
                            className="bg-gray-50 border-gray-200"
                            data-testid="task-title-input"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-gray-600 text-sm mb-2 font-medium">Description</label>
                          <textarea
                            value={taskForm.description}
                            onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                            placeholder="Enter task description"
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm"
                            rows={3}
                            data-testid="task-description-input"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-gray-600 text-sm mb-2 font-medium">Assign To *</label>
                          <select
                            value={taskForm.assigned_to}
                            onChange={(e) => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm"
                            data-testid="task-assignee-select"
                          >
                            <option value="">Select team member</option>
                            {adminList.map(admin => (
                              <option key={admin.username} value={admin.username}>{getLocationFlag(admin.username)} {admin.name} ({admin.role})</option>
                            ))}
                            <option value={adminData?.username}>{getLocationFlag(adminData?.username)} {adminData?.name} (Me)</option>
                          </select>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-gray-600 text-sm mb-2 font-medium">Due Date</label>
                            <Input
                              type="date"
                              value={taskForm.due_date}
                              onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                              className="bg-gray-50 border-gray-200"
                              data-testid="task-due-date-input"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-gray-600 text-sm mb-2 font-medium">Priority</label>
                            <select
                              value={taskForm.priority}
                              onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm"
                              data-testid="task-priority-select"
                            >
                              <option value="High">High</option>
                              <option value="Medium">Medium</option>
                              <option value="Low">Low</option>
                            </select>
                          </div>
                        </div>
                        
                        <div className="flex gap-3 pt-4">
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => setShowTaskModal(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                            onClick={createTask}
                            disabled={!taskForm.title || !taskForm.assigned_to}
                            data-testid="create-task-submit-btn"
                          >
                            {editingTask ? 'Update Task' : 'Create Task'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Activity Tab */}
            {activeTab === 'activity' && (
              <div className="space-y-6">
                <h1 className="text-2xl font-bold text-gray-800">Activity</h1>
                
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
                  <div className="space-y-6">
                    {[
                      { action: 'New client registered', detail: 'George Anderson - Anderson Legal', time: '2 hours ago', icon: UserCheck },
                      { action: 'Project completed', detail: 'CRM Integration for Belfast Dental', time: '5 hours ago', icon: CheckCircle2 },
                      { action: 'Invoice sent', detail: '£2,500 to Patel Construction', time: 'Yesterday', icon: Send },
                      { action: 'Meeting scheduled', detail: 'Website review with Tech Solutions', time: '2 days ago', icon: Calendar },
                    ].map((item, idx) => {
                      const Icon = item.icon;
                      return (
                        <div key={idx} className="flex gap-4">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800">{item.action}</p>
                            <p className="text-sm text-gray-500 truncate">{item.detail}</p>
                            <p className="text-xs text-gray-400 mt-1">{item.time}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Communications Tab */}
            {activeTab === 'communications' && (
              <div className="space-y-6 h-full">
                {/* Chat Mode Toggle */}
                <div className="flex items-center gap-4">
                  <h1 className="text-2xl font-bold text-gray-800">Team Chat</h1>
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => { setChatMode('group'); fetchGroupMessages(); }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        chatMode === 'group' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-800'
                      }`}
                      data-testid="group-chat-toggle"
                    >
                      <Users className="w-4 h-4 inline mr-2" />
                      Team Room
                    </button>
                    <button
                      onClick={() => setChatMode('direct')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        chatMode === 'direct' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-800'
                      }`}
                      data-testid="direct-chat-toggle"
                    >
                      <MessageSquare className="w-4 h-4 inline mr-2" />
                      Direct Messages
                    </button>
                  </div>
                </div>

                {/* Group Chat (Team Room) */}
                {chatMode === 'group' && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 280px)' }}>
                    <div className="px-4 sm:px-6 py-3 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
                      <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                        <Users className="w-5 h-5 text-purple-600" />
                        Team Room
                      </h3>
                      <p className="text-sm text-gray-500">Shared chat room for all administrators</p>
                    </div>
                    
                    {/* Group Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {groupMessages.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No messages yet</p>
                          <p className="text-sm">Start the team conversation!</p>
                        </div>
                      ) : (
                        groupMessages.map((msg) => {
                          const isMe = msg.sender_username === adminData?.name?.toLowerCase() || 
                                       msg.sender_name?.toLowerCase() === adminData?.name?.toLowerCase();
                          return (
                            <div key={msg.message_id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[80%] ${isMe ? 'order-2' : ''}`}>
                                {!isMe && (
                                  <p className="text-xs text-gray-500 mb-1 ml-2">{msg.sender_name}</p>
                                )}
                                <div className={`px-4 py-2 rounded-2xl ${
                                  isMe 
                                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-br-md' 
                                    : 'bg-gray-100 text-gray-800 rounded-bl-md'
                                }`}>
                                  {msg.content && <p className="text-sm">{msg.content}</p>}
                                  {msg.file_url && (
                                    <div className="mt-2">
                                      {msg.file_type?.startsWith('image/') ? (
                                        <img src={msg.file_url} alt="Shared" className="max-w-full rounded-lg max-h-48 object-cover" />
                                      ) : (
                                        <a href={msg.file_url} target="_blank" rel="noopener noreferrer" 
                                           className={`flex items-center gap-2 ${isMe ? 'text-white/90' : 'text-blue-600'}`}>
                                          <File className="w-4 h-4" />
                                          {msg.file_name || 'Download File'}
                                        </a>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <p className={`text-xs text-gray-400 mt-1 ${isMe ? 'text-right mr-2' : 'ml-2'}`}>
                                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    
                    {/* Group Message Input */}
                    <div className="p-4 border-t border-gray-100 bg-gray-50">
                      <div className="flex items-center gap-2">
                        <label className="p-2 text-gray-500 hover:text-blue-600 cursor-pointer">
                          <Paperclip className="w-5 h-5" />
                          <input 
                            type="file" 
                            className="hidden" 
                            onChange={(e) => setSelectedFile(e.target.files[0])}
                          />
                        </label>
                        <Input
                          value={groupNewMessage}
                          onChange={(e) => setGroupNewMessage(e.target.value)}
                          placeholder="Message the team..."
                          className="flex-1 bg-white"
                          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendGroupMessage()}
                          data-testid="group-message-input"
                        />
                        <Button
                          onClick={sendGroupMessage}
                          disabled={sendingMessage || (!groupNewMessage.trim() && !selectedFile)}
                          className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                          data-testid="send-group-message-btn"
                        >
                          {sendingMessage ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </div>
                      {selectedFile && (
                        <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                          <File className="w-4 h-4" />
                          <span className="truncate">{selectedFile.name}</span>
                          <button onClick={() => setSelectedFile(null)} className="text-red-500 hover:text-red-600">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Direct Messages Mode */}
                {chatMode === 'direct' && !selectedChat && (
                  // Conversation List View
                  <>
                    {/* Admin List - Start New Conversation */}
                    {adminList.length > 0 && (
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-4 sm:px-6 py-3 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-cyan-50">
                          <h3 className="font-semibold text-gray-800">Team Members</h3>
                          <p className="text-sm text-gray-500">Click to start a conversation</p>
                        </div>
                        <div className="p-4 flex flex-wrap gap-3">
                          {adminList.map((admin) => (
                            <button
                              key={admin.username}
                              onClick={() => openChat(admin.username)}
                              className="flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-blue-50 rounded-xl border border-gray-200 hover:border-blue-300 transition-all"
                              data-testid={`chat-with-${admin.username}`}
                            >
                              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                                {admin.name?.charAt(0) || 'A'}
                              </div>
                              <div className="text-left">
                                <p className="font-medium text-gray-800">{getLocationFlag(admin.username)} {admin.name}</p>
                                <p className="text-xs text-gray-500">{admin.role} • {getLocationName(admin.username)}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Existing Conversations */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                      <div className="px-4 sm:px-6 py-3 border-b border-gray-100">
                        <h3 className="font-semibold text-gray-800">Recent Conversations</h3>
                      </div>
                      {conversations.length === 0 ? (
                        <div className="px-6 py-12 text-center text-gray-500">
                          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No conversations yet</p>
                          <p className="text-sm">Click on a team member above to start chatting</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {conversations.map((conv) => (
                            <button
                              key={conv.partner_username}
                              onClick={() => openChat(conv.partner_username)}
                              className={`w-full px-4 sm:px-6 py-4 hover:bg-gray-50 text-left transition-colors ${conv.unread_count > 0 ? 'bg-blue-50/50' : ''}`}
                              data-testid={`conversation-${conv.partner_username}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                                  {conv.partner_name?.charAt(0) || 'A'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className={`font-medium ${conv.unread_count > 0 ? 'text-gray-900' : 'text-gray-700'}`}>{conv.partner_name}</p>
                                    {conv.unread_count > 0 && (
                                      <span className="px-2 py-0.5 bg-blue-500 text-white text-xs font-medium rounded-full">
                                        {conv.unread_count}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500">{conv.partner_role}</p>
                                  <p className={`text-sm truncate ${conv.unread_count > 0 ? 'font-medium text-gray-800' : 'text-gray-500'}`}>
                                    {conv.last_message}
                                  </p>
                                </div>
                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                  {new Date(conv.last_message_time).toLocaleDateString()}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                {/* Direct Message Chat View */}
                {chatMode === 'direct' && selectedChat && (
                  // Chat View
                  <div className="flex flex-col h-[calc(100vh-200px)] bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    {/* Chat Header */}
                    <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-500 to-cyan-500 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={closeChat}
                        className="text-white hover:bg-white/20 -ml-2"
                        data-testid="close-chat-btn"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </Button>
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
                        {chatPartner?.name?.charAt(0) || 'A'}
                      </div>
                      <div>
                        <p className="font-semibold text-white">{chatPartner?.name || selectedChat}</p>
                        <p className="text-xs text-blue-100">{chatPartner?.role} - {chatPartner?.clearance_level}</p>
                      </div>
                    </div>
                    
                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                      {chatLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                        </div>
                      ) : chatMessages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                          <MessageSquare className="w-12 h-12 mb-3 opacity-50" />
                          <p>No messages yet</p>
                          <p className="text-sm">Start the conversation!</p>
                        </div>
                      ) : (
                        chatMessages.map((msg, idx) => {
                          const isMe = msg.sender_username === adminData?.name?.toLowerCase() || 
                                       msg.sender_name === adminData?.name;
                          return (
                            <div
                              key={msg.message_id || idx}
                              className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`max-w-[80%] sm:max-w-[70%] ${isMe ? 'order-2' : ''}`}>
                                {!isMe && (
                                  <p className="text-xs text-gray-500 mb-1 ml-1">{msg.sender_name}</p>
                                )}
                                <div
                                  className={`px-4 py-3 rounded-2xl ${
                                    isMe
                                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-br-md'
                                      : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
                                  }`}
                                >
                                  {/* File attachment */}
                                  {msg.file_url && (
                                    <div className="mb-2">
                                      {msg.file_type === 'image' ? (
                                        <img 
                                          src={`${BACKEND_URL}${msg.file_url}`}
                                          alt={msg.file_name}
                                          className="max-w-full max-h-48 rounded-lg cursor-pointer"
                                          onClick={() => window.open(`${BACKEND_URL}${msg.file_url}`, '_blank')}
                                        />
                                      ) : (
                                        <a
                                          href={`${BACKEND_URL}${msg.file_url}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`flex items-center gap-2 p-2 rounded-lg ${isMe ? 'bg-white/20 hover:bg-white/30' : 'bg-gray-100 hover:bg-gray-200'}`}
                                        >
                                          <File className="w-5 h-5" />
                                          <span className="text-sm truncate">{msg.file_name}</span>
                                          <Download className="w-4 h-4 flex-shrink-0" />
                                        </a>
                                      )}
                                    </div>
                                  )}
                                  {msg.content && !msg.content.startsWith('Shared a file:') && (
                                    <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                                  )}
                                </div>
                                <p className={`text-xs text-gray-400 mt-1 ${isMe ? 'text-right mr-1' : 'ml-1'}`}>
                                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    
                    {/* Selected File Preview */}
                    {selectedFile && (
                      <div className="px-4 py-2 border-t border-gray-100 bg-blue-50 flex items-center gap-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {selectedFile.file_type === 'image' ? (
                            <Image className="w-5 h-5 text-blue-600" />
                          ) : (
                            <File className="w-5 h-5 text-blue-600" />
                          )}
                          <span className="text-sm text-gray-700 truncate">{selectedFile.file_name}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedFile(null)}
                          className="text-gray-500 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    
                    {/* Message Input with File Attachment */}
                    <div className="flex items-center gap-2 px-4 py-3 border-t border-gray-100 bg-white flex-shrink-0">
                      <input
                        type="file"
                        id="file-upload"
                        className="hidden"
                        onChange={handleFileUpload}
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => document.getElementById('file-upload').click()}
                        disabled={uploadingFile}
                        className="text-gray-500 hover:text-blue-600"
                        data-testid="attach-file-btn"
                      >
                        {uploadingFile ? (
                          <RefreshCw className="w-5 h-5 animate-spin" />
                        ) : (
                          <Paperclip className="w-5 h-5" />
                        )}
                      </Button>
                      <Input
                        type="text"
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendMessageWithFile();
                          }
                        }}
                        className="flex-1 bg-gray-50 border-gray-200"
                        data-testid="message-input"
                      />
                      <Button
                        onClick={sendMessageWithFile}
                        disabled={(!newMessage.trim() && !selectedFile) || sendingMessage}
                        className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-4"
                        data-testid="send-message-btn"
                      >
                        {sendingMessage ? (
                          <RefreshCw className="w-5 h-5 animate-spin" />
                        ) : (
                          <Send className="w-5 h-5" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <h1 className="text-2xl font-bold text-gray-800">Analytics</h1>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-500">Total Clients</p>
                      <Users className="w-5 h-5 text-blue-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{totalBookings}</p>
                    <p className="text-sm text-gray-500">Active clients</p>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-500">Tasks Completed</p>
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{taskStats.completed || 0}</p>
                    <p className="text-sm text-gray-500">of {taskStats.total || 0} total</p>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-500">Tasks In Progress</p>
                      <Clock className="w-5 h-5 text-amber-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{taskStats.inProgress || 0}</p>
                    <p className="text-sm text-gray-500">Active tasks</p>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-500">Sprint Progress</p>
                      <Rocket className="w-5 h-5 text-purple-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{sprintData?.overall_progress?.percent || 0}%</p>
                    <p className="text-sm text-gray-500">30-day sprint</p>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
                  <h3 className="font-semibold text-gray-800 mb-4">Task Completion by Priority</h3>
                  <div className="space-y-4">
                    {['High', 'Medium', 'Low'].map((priority) => {
                      const priorityTasks = tasks.filter(t => t.priority === priority);
                      const completed = priorityTasks.filter(t => t.status === 'Completed').length;
                      const total = priorityTasks.length;
                      const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
                      return (
                        <div key={priority}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{priority} Priority</span>
                            <span className="text-gray-800 font-medium">{completed}/{total}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full transition-all ${
                                priority === 'High' ? 'bg-red-500' :
                                priority === 'Medium' ? 'bg-amber-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {tasks.length === 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
                    <BarChart3 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No analytics data yet</p>
                    <p className="text-sm text-gray-400">Start adding tasks and clients to see analytics</p>
                  </div>
                )}
              </div>
            )}

            {/* Billing Tab */}
            {activeTab === 'billing' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <h1 className="text-2xl font-bold text-gray-800">Billing</h1>
                  <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg w-full sm:w-auto">
                    <Plus className="w-4 h-4 mr-2" /> Create Invoice
                  </Button>
                </div>
                
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
                  <CreditCard className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No Invoices Yet</h3>
                  <p className="text-gray-500 mb-4">Create your first invoice to start tracking payments</p>
                  <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Create First Invoice
                  </Button>
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
                
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100">
                  <div className="p-4 sm:p-6">
                    <h3 className="font-semibold text-gray-800 mb-4">Profile Settings</h3>
                    <div className="space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center text-white text-2xl font-bold">
                          {adminData?.name?.charAt(0) || 'A'}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">{getLocationFlag(adminData?.username)} {adminData?.name}</p>
                          <p className="text-sm text-gray-500">{adminData?.role} • {getLocationName(adminData?.username)}</p>
                        </div>
                        <Button variant="outline" className="w-full sm:w-auto">Edit Profile</Button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 sm:p-6">
                    <h3 className="font-semibold text-gray-800 mb-4">Security</h3>
                    <div className="space-y-3">
                      <Button variant="outline" className="w-full sm:w-auto">Change Password</Button>
                      <p className="text-sm text-gray-500">Last password change: Never</p>
                    </div>
                  </div>
                  
                  <div className="p-4 sm:p-6">
                    <h3 className="font-semibold text-gray-800 mb-4">Notifications</h3>
                    <div className="space-y-3">
                      {['Email notifications', 'SMS alerts', 'Browser notifications'].map((item) => (
                        <label key={item} className="flex items-center justify-between py-2 cursor-pointer">
                          <span className="text-gray-700">{item}</span>
                          <input type="checkbox" defaultChecked className="w-5 h-5 rounded border-gray-300 text-blue-500 focus:ring-blue-500" />
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Admin Permissions Panel - Super Admin Only */}
                {adminData?.clearance_level === 'FOUNDER' && (
                  <>
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50 flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                            <Shield className="w-5 h-5 text-amber-600" />
                            Admin Permissions Panel
                          </h3>
                          <p className="text-sm text-gray-500">Manage admin accounts and permissions</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { fetchAllAdmins(); fetchAuditLogs(); }} data-testid="refresh-admins">
                          <RefreshCw className={`w-4 h-4 ${adminManagementLoading ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                      
                      {adminManagementLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {allAdmins.map((admin) => (
                            <div key={admin.username} className={`px-4 sm:px-6 py-4 ${!admin.is_active ? 'bg-gray-50 opacity-60' : ''}`}>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                <div className="flex items-center gap-3 flex-1">
                                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                                    admin.clearance_level === 'FOUNDER' 
                                      ? 'bg-gradient-to-r from-amber-500 to-orange-500' 
                                      : 'bg-gradient-to-r from-blue-500 to-cyan-500'
                                  }`}>
                                    {admin.name?.charAt(0) || 'A'}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-800">
                                      {getLocationFlag(admin.username)} {admin.name}
                                    </p>
                                    <p className="text-sm text-gray-500">@{admin.username} • {getLocationName(admin.username)}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                        admin.clearance_level === 'FOUNDER' 
                                          ? 'bg-amber-100 text-amber-700' 
                                          : 'bg-blue-100 text-blue-700'
                                      }`}>{admin.role}</span>
                                      {!admin.is_active && (
                                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-600">INACTIVE</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {admin.username !== adminData?.username && admin.clearance_level !== 'FOUNDER' && (
                                  <div className="flex items-center gap-2">
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={() => setEditingAdmin(admin.username)}
                                      data-testid={`edit-admin-${admin.username}`}
                                    >
                                      <Edit className="w-4 h-4 mr-1" /> Edit
                                    </Button>
                                    {admin.is_active && (
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                        onClick={() => deactivateAdmin(admin.username)}
                                        data-testid={`deactivate-admin-${admin.username}`}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {admin.phone && (
                                <div className="mt-2 ml-15 text-sm text-gray-500">
                                  <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {admin.phone}</span>
                                  {admin.work_email && <span className="ml-4 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {admin.work_email}</span>}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Audit Log */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                      <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
                        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                          <Activity className="w-5 h-5 text-blue-600" />
                          Admin Activity Log
                        </h3>
                        <p className="text-sm text-gray-500">Login history and permission changes</p>
                      </div>
                      
                      <div className="max-h-80 overflow-y-auto">
                        {auditLogs.length === 0 ? (
                          <div className="px-6 py-12 text-center text-gray-500">
                            <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No activity logs yet</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {auditLogs.map((log, idx) => (
                              <div key={idx} className="px-4 sm:px-6 py-3 flex items-start gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  log.event === 'admin_login_success' ? 'bg-green-100 text-green-600' :
                                  log.event === 'admin_login_failed' ? 'bg-red-100 text-red-600' :
                                  log.event === 'admin_profile_setup_completed' ? 'bg-blue-100 text-blue-600' :
                                  log.action?.includes('deactivated') ? 'bg-red-100 text-red-600' :
                                  'bg-amber-100 text-amber-600'
                                }`}>
                                  {log.event === 'admin_login_success' ? <LogIn className="w-4 h-4" /> :
                                   log.event === 'admin_login_failed' ? <AlertCircle className="w-4 h-4" /> :
                                   log.event === 'admin_profile_setup_completed' ? <UserCheck className="w-4 h-4" /> :
                                   <Shield className="w-4 h-4" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-800">
                                    {log.event === 'admin_login_success' ? `${log.username} logged in` :
                                     log.event === 'admin_login_failed' ? `Failed login attempt for ${log.username}` :
                                     log.event === 'admin_profile_setup_completed' ? `${log.username} completed profile setup` :
                                     log.action === 'admin_permissions_updated' ? `${log.changed_by} updated ${log.target_username}'s permissions` :
                                     log.action === 'admin_deactivated' ? `${log.changed_by} deactivated ${log.target_username}` :
                                     log.action || log.event}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {new Date(log.timestamp).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Client Detail Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-500 to-cyan-500 flex-shrink-0">
              <div>
                <h3 className="text-white font-semibold text-lg">Client Details</h3>
                <p className="text-blue-100 text-sm">{selectedBooking.client_id}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedBooking(null)} className="text-white hover:bg-white/20" data-testid="close-detail-modal">
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-4 sm:p-6 space-y-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-500 text-sm">Full Name</label>
                  <p className="text-gray-800 font-medium">{selectedBooking.name}</p>
                </div>
                <div>
                  <label className="text-gray-500 text-sm">Company</label>
                  <p className="text-gray-800 font-medium">{selectedBooking.company_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-gray-500 text-sm">Email</label>
                  <p className="text-blue-600 font-medium break-all">{selectedBooking.email}</p>
                </div>
                <div>
                  <label className="text-gray-500 text-sm">Phone</label>
                  <p className="text-gray-800">{selectedBooking.phone || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-gray-500 text-sm">Industry</label>
                  <p className="text-gray-800">{selectedBooking.business_category || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-gray-500 text-sm">Booking Reference</label>
                  <p className="text-amber-600 font-mono font-medium">{selectedBooking.booking_reference || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-gray-500 text-sm">Preferred Date</label>
                  <p className="text-gray-800">{selectedBooking.preferred_date || 'To be confirmed'}</p>
                </div>
                <div>
                  <label className="text-gray-500 text-sm">Preferred Time</label>
                  <p className="text-gray-800">{selectedBooking.preferred_time || 'To be confirmed'}</p>
                </div>
              </div>
              
              {selectedBooking.notes && (
                <div>
                  <label className="text-gray-500 text-sm">Notes</label>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg mt-1 border border-gray-100">{selectedBooking.notes}</p>
                </div>
              )}
              
              <div>
                <label className="text-gray-500 text-sm mb-2 block">Update Status</label>
                <div className="flex flex-wrap gap-2">
                  {['confirmed', 'contacted', 'completed', 'cancelled'].map((status) => (
                    <Button
                      key={status}
                      size="sm"
                      variant={selectedBooking.status === status ? 'default' : 'outline'}
                      className={selectedBooking.status === status ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white' : ''}
                      onClick={() => updateStatus(selectedBooking.client_id, status)}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100">
                <Button className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white" onClick={() => window.location.href = `mailto:${selectedBooking.email}`}>
                  <Mail className="w-4 h-4 mr-2" /> Send Email
                </Button>
                {selectedBooking.phone && (
                  <Button className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white" onClick={() => window.location.href = `tel:${selectedBooking.phone}`}>
                    <Phone className="w-4 h-4 mr-2" /> Call Client
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Jarvis Voice Assistant */}
      <div className="fixed bottom-6 right-6 z-[300]">
        {/* Jarvis Chat Panel */}
        {jarvisOpen && (
          <div className="absolute bottom-20 right-0 w-80 sm:w-[420px] bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl border border-gray-700 overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot className="w-6 h-6 text-white" />
                <div>
                  <h3 className="text-white font-bold">JARVIS</h3>
                  <p className="text-amber-100 text-xs">AI with Memory</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setJarvisShowMemory(!jarvisShowMemory)} 
                  className={`text-white hover:bg-white/20 ${jarvisShowMemory ? 'bg-white/20' : ''}`}
                  title="View Memories"
                >
                  <FileText className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setJarvisOpen(false)} className="text-white hover:bg-white/20">
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            <div className="p-4">
              {/* Memory Panel */}
              {jarvisShowMemory ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-white font-medium text-sm">My Memories</h4>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={clearJarvisMemories}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20 text-xs"
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Clear All
                    </Button>
                  </div>
                  
                  <div className="bg-gray-800/50 rounded-xl p-3 max-h-[300px] overflow-y-auto">
                    {jarvisMemories.length === 0 ? (
                      <div className="text-center py-6">
                        <FileText className="w-10 h-10 mx-auto text-gray-600 mb-2" />
                        <p className="text-gray-400 text-sm">No memories yet</p>
                        <p className="text-gray-500 text-xs mt-1">I'll remember important things you tell me!</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {Object.entries(
                          jarvisMemories.reduce((acc, mem) => {
                            const cat = mem.category || 'general';
                            if (!acc[cat]) acc[cat] = [];
                            acc[cat].push(mem);
                            return acc;
                          }, {})
                        ).map(([category, mems]) => (
                          <div key={category} className="mb-3">
                            <p className="text-amber-400 text-xs font-medium uppercase mb-1">{category}</p>
                            {mems.map((mem, idx) => (
                              <div key={idx} className="text-gray-300 text-sm py-1 pl-2 border-l-2 border-gray-700">
                                {mem.content}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <p className="text-gray-500 text-xs text-center">
                    Tip: Say "Remember that..." to save something important
                  </p>
                  
                  <Button 
                    onClick={() => setJarvisShowMemory(false)}
                    className="w-full bg-gray-700 hover:bg-gray-600 text-white"
                  >
                    Back to Chat
                  </Button>
                </div>
              ) : (
                <>
                  {/* Jarvis Human Face */}
                  <div className="flex justify-center mb-4">
                    <div className="relative">
                      {/* Face Container with glow effect */}
                      <div 
                        className={`relative transition-transform duration-300 ${jarvisSpeaking || jarvisStreaming ? 'scale-105' : ''}`}
                        style={{
                          animation: jarvisSpeaking || jarvisStreaming ? 'jarvis-bob 2s ease-in-out infinite' : 'none'
                        }}
                      >
                        {/* Glow ring when active */}
                        {(jarvisSpeaking || jarvisStreaming || jarvisThinking) && (
                          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 blur-md animate-pulse" style={{ margin: '-4px' }} />
                        )}
                        
                        {/* Face image */}
                        <img 
                          src={JARVIS_FACE_URL} 
                          alt="Jarvis" 
                          className="w-28 h-28 rounded-full object-cover border-4 border-gray-700 shadow-xl relative z-10"
                          style={{
                            filter: jarvisThinking ? 'brightness(0.9)' : 'brightness(1)'
                          }}
                        />
                        
                        {/* Speaking indicator overlay */}
                        {(jarvisSpeaking || jarvisStreaming) && (
                          <div className="absolute inset-0 rounded-full border-4 border-green-500 animate-pulse z-20" />
                        )}
                      </div>
                      
                      {/* Status indicator */}
                      <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-medium shadow-lg z-30 ${
                        jarvisListening ? 'bg-red-500 text-white animate-pulse' :
                        jarvisThinking ? 'bg-amber-500 text-white animate-pulse' :
                        jarvisSpeaking || jarvisStreaming ? 'bg-green-500 text-white' :
                        'bg-gray-700 text-gray-300'
                      }`}>
                        {jarvisListening ? 'Listening...' : 
                         jarvisThinking ? 'Thinking...' : 
                         jarvisSpeaking ? 'Speaking...' : 
                         jarvisStreaming ? 'Typing...' : 'Ready'}
                      </div>
                    </div>
                  </div>
              
              {/* Response Display */}
              <div className="bg-gray-800/50 rounded-xl p-4 mb-4 min-h-[80px] max-h-[150px] overflow-y-auto">
                <p className="text-gray-300 text-sm leading-relaxed">
                  {jarvisResponse}
                  {jarvisStreaming && <span className="inline-block w-2 h-4 bg-amber-500 ml-1 animate-pulse" />}
                </p>
              </div>
              
              {/* Voice Control */}
              <div className="flex items-center justify-center gap-4 mb-4">
                <button
                  onClick={startJarvisListening}
                  disabled={jarvisListening || jarvisStreaming}
                  className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                    jarvisListening 
                      ? 'bg-red-500 animate-pulse scale-110' 
                      : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:scale-105'
                  } ${jarvisStreaming ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid="jarvis-mic-btn"
                >
                  {jarvisListening ? (
                    <MicOff className="w-6 h-6 text-white" />
                  ) : (
                    <Mic className="w-6 h-6 text-white" />
                  )}
                </button>
              </div>
              
              {/* Text Input */}
              <div className="flex gap-2">
                <Input
                  value={jarvisMessage}
                  onChange={(e) => setJarvisMessage(e.target.value)}
                  placeholder="Ask Jarvis anything..."
                  className="flex-1 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                  onKeyDown={(e) => e.key === 'Enter' && !jarvisStreaming && sendJarvisTextMessage()}
                  disabled={jarvisStreaming}
                  data-testid="jarvis-text-input"
                />
                <Button 
                  onClick={sendJarvisTextMessage}
                  disabled={jarvisStreaming || !jarvisMessage.trim()}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                  data-testid="jarvis-send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Quick Commands */}
              <div className="mt-3 pt-3 border-t border-gray-700">
                <div className="flex flex-wrap gap-1">
                  {['Lead tips', 'Email help', 'Sprint goals', 'Follow-up advice'].map(cmd => (
                    <button
                      key={cmd}
                      onClick={() => !jarvisStreaming && handleJarvisCommand(cmd)}
                      disabled={jarvisStreaming}
                      className={`px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors ${jarvisStreaming ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {cmd}
                    </button>
                  ))}
                </div>
              </div>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Floating Jarvis Button */}
        <button
          onClick={() => setJarvisOpen(!jarvisOpen)}
          className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 ${
            jarvisOpen 
              ? 'bg-gray-800 border-2 border-amber-500' 
              : 'bg-gradient-to-r from-amber-500 to-orange-500'
          }`}
          data-testid="jarvis-toggle-btn"
        >
          {jarvisOpen ? (
            <X className="w-6 h-6 text-amber-500" />
          ) : (
            <Bot className="w-6 h-6 text-white" />
          )}
        </button>
      </div>
    </div>
  );
};

export default AdminBackend;
